import i18n from '@/utils/i18n';
import React, { useEffect, useState } from 'react';
import { getOperationName } from 'apollo-utilities';
import { gql, useMutation } from '@apollo/client';
import {
  LOCATION_MANAGER_ROUTES,
  ORDER_GUIDE_ROUTES,
} from '@/navigators/routes';
import { LocationManagerStackParams } from '@/navigators/types';
import {
  MenuAi_GetItemsFromMenuImagesQuery,
  MenuAi_GetItemsFromMenuImagesQueryVariables,
  MenuItemInput,
  OrderGuide_CreateOrderGuideFromGeneratedMenuItemsMutation,
  OrderGuide_CreateOrderGuideFromGeneratedMenuItemsMutationVariables,
} from '@/types';
import { MULTI_ORDER_GUIDE_QUERY } from '@/contexts/items-context-queries';
import { StackScreenProps } from '@react-navigation/stack';
import { UPLOAD_PREFIXES, uploadPublicImage } from '@/services/s3';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useLazyQuery } from '@/hooks/use-lazy-query';
import { useToasts } from '@/contexts/toast-context';
import { v4 as uuidv4 } from 'uuid';
import { ViewMenuModal } from '@/components/locations/view-menu-modal';
import { YelpMenuItem } from '@/components/locations/types';

type Props = StackScreenProps<
  LocationManagerStackParams,
  LOCATION_MANAGER_ROUTES.ViewImageGeneratedMenu
>;

export const GET_ITEMS_FROM_MENU_IMAGES = gql`
  query MenuAi_GetItemsFromMenuImages($imageUrls: [String!]!) {
    getItemsFromMenuImages(image_urls: $imageUrls) {
      items {
        item_name
        ingredients
        category
      }
    }
  }
`;

export const CREATE_ORDER_GUIDE_FROM_MENU_ITEMS = gql`
  mutation OrderGuide_CreateOrderGuideFromGeneratedMenuItems(
    $items: [MenuItemInput!]!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    createOrderGuideFromGeneratedMenuItems(
      items: $items
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      order_guide_collection_uuid
      order_guide_partition_uuid
    }
  }
`;

export const ViewImageGeneratedMenuScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const { imageURIs, restaurantName, restaurantUUID, supplierUUID } =
    route.params;
  const { showErrorToast } = useToasts();

  const [menuLoadingMessage, setMenuLoadingMessage] = useState<string>('');
  const [isImagesProcessing, setIsImagesProcessing] = useState<boolean>(false);
  const [isImagesProcessed, setIsImagesProcessed] = useState<boolean>(false);
  const [menuItems, setMenuItems] = useState<YelpMenuItem[]>([]);

  const { selectRestaurant } = useAuthenticatedUserContext();

  const getItemsFromImages = useLazyQuery<
    MenuAi_GetItemsFromMenuImagesQuery,
    MenuAi_GetItemsFromMenuImagesQueryVariables
  >(GET_ITEMS_FROM_MENU_IMAGES, { fetchPolicy: 'no-cache' });

  const [createOrderGuide, { loading: isCreateOrderGuideLoading }] =
    useMutation<
      OrderGuide_CreateOrderGuideFromGeneratedMenuItemsMutation,
      OrderGuide_CreateOrderGuideFromGeneratedMenuItemsMutationVariables
    >(CREATE_ORDER_GUIDE_FROM_MENU_ITEMS, {
      awaitRefetchQueries: true,
      refetchQueries: [getOperationName(MULTI_ORDER_GUIDE_QUERY) as string],
    });

  useEffect(() => {
    const processImages = async () => {
      setIsImagesProcessing(true);
      setMenuLoadingMessage(i18n.t('uploading_images'));
      const imageUrls: string[] = [];
      for (const imageURI of imageURIs) {
        const imageUrl = await uploadPublicImage(
          uuidv4(),
          imageURI,
          UPLOAD_PREFIXES.MenuImages,
        );
        imageUrls.push(imageUrl);
      }

      setMenuLoadingMessage(i18n.t('scanning_images'));
      const { data } = await getItemsFromImages({
        variables: { imageUrls: imageUrls },
      });
      setIsImagesProcessed(true);
      setIsImagesProcessing(false);

      // Fill out required `category` attribute before saving
      const menuItems: YelpMenuItem[] =
        data?.getItemsFromMenuImages?.items?.map((item) => ({
          ...item,
          category: i18n.t('items'),
        }));

      setMenuItems(menuItems);
    };

    try {
      if (!isImagesProcessing && !isImagesProcessed) {
        processImages();
      }
    } catch (e) {
      showErrorToast();
    }
  }, [
    getItemsFromImages,
    showErrorToast,
    imageURIs,
    isImagesProcessed,
    isImagesProcessing,
  ]);

  const generateOrderGuide = async () => {
    try {
      const items: MenuItemInput[] = menuItems.map((item) => ({
        ingredients: item.ingredients,
        item_name: item.item_name,
      }));

      const { data } = await createOrderGuide({
        variables: {
          items,
          restaurantUUID,
          supplierUUID,
        },
      });

      selectRestaurant(restaurantUUID);
      navigation.popToTop();
      navigation.goBack();
      navigation.navigate(ORDER_GUIDE_ROUTES.Base, {
        orderGuideCollectionUUID:
          data?.createOrderGuideFromGeneratedMenuItems
            ?.order_guide_collection_uuid,
      });
    } catch (e) {
      showErrorToast();
    }
  };

  return (
    <ViewMenuModal
      generateOrderGuideLoading={isCreateOrderGuideLoading}
      menuItems={menuItems}
      menuItemsLoading={isImagesProcessing}
      menuItemsLoadingMessage={menuLoadingMessage}
      noMenuItemsMessage={i18n.t('no_menu_items_generated_message')}
      noMenuItemsTitle={i18n.t('no_menu_items_generated_title')}
      onGenerateOrderGuide={generateOrderGuide}
      restaurantName={restaurantName}
      restaurantUUID={restaurantUUID}
      showGenerateOrderGuideButton={menuItems.length > 0}
    />
  );
};
